import { useEffect } from 'react';
import { helpUrl } from '../lib/helpUrl';

/**
 * Tag names of elements where typing should NOT trigger the global `?`
 * shortcut. Anything else (buttons, links, plain divs, the document body) is
 * fair game.
 */
const TYPING_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

/**
 * Returns `true` when the current `activeElement` is somewhere the user could
 * reasonably be typing — an `<input>`, `<textarea>`, `<select>`, or any
 * `contentEditable` host. Firing a navigation shortcut while the user is
 * mid-typed-`?` would be disruptive, so the global handler bails in those
 * cases.
 */
function isTypingTarget(el: Element | null): boolean {
    if (!el) return false;
    if (TYPING_TAGS.has(el.tagName)) return true;
    const htmlEl = el as HTMLElement;
    // `isContentEditable` is the canonical browser API, but jsdom (used in
    // unit tests) does not implement it — fall back to the reflected
    // `contentEditable` property and the raw attribute so both environments
    // see the same answer.
    if (htmlEl.isContentEditable) return true;
    if (htmlEl.contentEditable === 'true' || htmlEl.contentEditable === 'plaintext-only') return true;
    const attr = el.getAttribute?.('contenteditable');
    if (attr === '' || attr === 'true' || attr === 'plaintext-only') return true;
    return false;
}

/**
 * Registers a global `keydown` listener that opens the help center (`/help`
 * TOC) in a new tab when the user presses `?` (Shift+/) anywhere in the app —
 * provided they aren't currently typing into an input, textarea, select, or
 * contentEditable region. Mirrors the header Help button's click behavior so
 * the two surfaces stay in lockstep.
 *
 * The listener is attached once on mount and cleaned up on unmount; meant to
 * be called once at the authenticated layout level.
 */
export function useGlobalHelpShortcut(): void {
    useEffect(() => {
        function onKeyDown(event: KeyboardEvent) {
            if (event.key !== '?') return;
            if (event.defaultPrevented) return;
            if (isTypingTarget(document.activeElement)) return;

            window.open(helpUrl(''), '_blank', 'noopener,noreferrer');
        }

        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
        };
    }, []);
}
